'use client'

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  createFormHook,
  createFormHookContexts,
  revalidateLogic,
  useStore,
} from '@tanstack/react-form'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import { Separator } from '@/components/common/Separator'
import { TEXT_INPUT_STYLES } from '@/components/common/TextInput'
import { Input, SearchInput } from '@/components/common/TextInputV2'
import ErrorMessage from '@/components/form/ErrorMessage'
import Label from '@/components/form/Label'
import type { AnyTanstackAppForm } from '@/components/form/tanstackFormTypes'
import * as EditField from '@/components/library-v2/catalog/ui/Field/Edit'
import { OrgBadge } from '@/components/pursuits/dashboard/ui/OrgBadge'
import { useOptionalOrgsOverview } from '@/components/pursuits/orgs-overview/context'
import {
  buildDefaultValuesFromExtendedSchema,
  buildExtendedValuesZodObject,
  type ExtendedValuesZodObject,
  prepareExtendedValuesZodObject,
} from '@/components/pursuits/shared/extendedSchemaFields'
import { createPursuitFieldControls } from '@/components/pursuits/v3/dialogs/FieldControls'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import {
  getStoredExtendedValues,
  isExtendedFieldEdited,
  mergeEditedExtendedValues,
} from '@/helpers/extended-schema'
import { getTerminalStatusForStage } from '@/helpers/pursuit'

import { useLatestPursuitSchema } from '@/hooks/pursuits/schemas/usePursuitSchemas'
import { usePursuitStageDueDates } from '@/hooks/pursuits/usePursuitStageDueDates'
import { usePursuitStages } from '@/hooks/pursuits/usePursuitStages'
import { useUpdatePursuit } from '@/hooks/pursuits/useUpdatePursuit'

import type { Color } from '@/types/Color'
import type {
  ExtendedFieldValues,
  ExtendedJsonSchema,
  ExtendedSchemaDerivedField,
} from '@/types/extended-schema'
import type { UUID } from '@/types/Id'
import type {
  CurrencyFieldDerivedV1,
  DropdownFieldDerivedV1,
  MultiSelectFieldDerivedV1,
  NumberFieldDerivedV1,
} from '@/types/library/ExtendedSchema/v1'
import type { Pursuit, PursuitType } from '@/types/pursuit/Pursuit'
import type { PursuitStage } from '@/types/pursuit/Stage'
import type {
  PursuitStageDueDate,
  PursuitStageDueDateSet,
} from '@/types/pursuit/StageDueDate'

interface EditPursuitDialogProps {
  open: boolean
  initialValues: Pursuit
  onOpenChange: (open: boolean) => void
  onSuccess?: (pursuit: Pursuit) => void
  scrollToFieldId?: string
}

// This object is the only validation the pursuit dialogs run, so the
// static rules (required title/stage, pwin bounds, ...) must apply even
// when the org has no extended schema — returning undefined for !schema
// used to disable validation entirely for schema-less orgs.
const pursuitFormShape: Record<
  Exclude<keyof EditPursuitFormValues, 'values'>,
  z.ZodTypeAny
> = {
  title: z.string().min(1, 'Title is required'),
  description: z.string().nullable().or(z.literal('')),
  stage_id: z.string().min(1, 'Stage is required'),
  user_ids: z.array(z.string()),
  due_date: z.date(),
  // Tolerant on purpose: the backend enum grows values the FE union
  // hasn't caught up to (intl_contract_pursuit exists on dev), the field
  // isn't user-editable here, and a strict enum would reject the hidden
  // value and make the whole pursuit uneditable.
  type: z.string().min(1),
  tag_ids: z.array(z.string()),
  estimated_value: z.number().nullable(),
  pwin: z.number().min(0).max(100).nullable(),
  has_unrestricted_access: z.boolean().optional(),
}

const buildPursuitFormZodObject = (
  values: ExtendedValuesZodObject,
): z.ZodObject<Record<keyof EditPursuitFormValues, z.ZodTypeAny>> =>
  z.object({ ...pursuitFormShape, values })

export const buildZodObjectFromExtendedSchema = (
  schema: ExtendedJsonSchema | undefined,
) =>
  buildPursuitFormZodObject(
    buildExtendedValuesZodObject(schema) ?? z.object({}),
  )

export function buildDefaultValuesFromPursuit(
  schema: ExtendedJsonSchema | undefined,
  pursuit: Pursuit | undefined,
): ExtendedFieldValues {
  if (!schema) return {}
  return buildDefaultValuesFromExtendedSchema(
    schema,
    getStoredExtendedValues(pursuit?.extended_data),
  )
}

// endregion

interface EditPursuitFormValues {
  title: string
  description: string
  stage_id: string
  user_ids: string[]
  due_date: Date
  type: PursuitType
  tag_ids: UUID[]
  estimated_value: number | null
  pwin: number | null
  has_unrestricted_access?: boolean
  values: ExtendedFieldValues
}

export const { fieldContext, useFieldContext, formContext, useFormContext } =
  createFormHookContexts()

// Register components with the form hook
const adaptedFieldComponents = EditField.createTanstackFormFieldComponents({
  useFieldContext,
})

const pursuitFieldControls = createPursuitFieldControls({ useFieldContext })

export const { useAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    ...adaptedFieldComponents,
    ...pursuitFieldControls,
  },
  formComponents: {},
})

interface OverviewSectionProps {
  form: AnyTanstackAppForm
  includeType?: boolean
  scrollToFieldId?: string
}

export const OverviewSection: React.FC<OverviewSectionProps> = ({
  form,
  includeType = true,
  scrollToFieldId,
}) => {
  return (
    <div className="flex flex-col gap-5">
      <form.AppField name="title">
        {(field) => (
          <div
            className="flex flex-col gap-2"
            data-field-id={scrollToFieldId === 'title' ? 'title' : undefined}
          >
            <Label required>Title</Label>
            <field.TitleFieldControl />
          </div>
        )}
      </form.AppField>

      <form.AppField name="description">
        {(field) => (
          <div
            className="flex flex-col gap-2"
            data-field-id={
              scrollToFieldId === 'description' ? 'description' : undefined
            }
          >
            <Label>Description</Label>
            <field.PursuitDescriptionFieldControl />
          </div>
        )}
      </form.AppField>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <form.AppField name="user_ids">
          {(field) => (
            <div
              className="flex flex-col gap-2"
              data-field-id={
                scrollToFieldId === 'user_ids' ? 'user_ids' : undefined
              }
            >
              <Label>Owners</Label>
              <field.AssigneesFieldControl />
            </div>
          )}
        </form.AppField>

        <form.AppField name="due_date">
          {(field) => (
            <div
              className="flex flex-col gap-2"
              data-field-id={
                scrollToFieldId === 'due_date' ? 'due_date' : undefined
              }
            >
              <Label required>Due Date</Label>
              <field.DueDateFieldControl />
            </div>
          )}
        </form.AppField>

        <form.AppField name="estimated_value">
          {(field) => (
            <div
              className="flex flex-col gap-2"
              data-field-id={
                scrollToFieldId === 'estimated_value'
                  ? 'estimated_value'
                  : undefined
              }
            >
              <Label>Estimated Value</Label>
              <field.EstimatedValueFieldControl />
            </div>
          )}
        </form.AppField>

        <form.AppField name="pwin">
          {(field) => (
            <div
              className="flex flex-col gap-2"
              data-field-id={scrollToFieldId === 'pwin' ? 'pwin' : undefined}
            >
              <Label>P(win)</Label>
              <div className="relative">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={
                    field.state.value != null ? String(field.state.value) : ''
                  }
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === '') {
                      field.handleChange(null)
                      return
                    }
                    if (!/^\d+$/.test(val)) return
                    field.handleChange(Math.min(Number(val), 100))
                  }}
                  onBlur={field.handleBlur}
                  className={twMerge('dark:bg-default-dark pr-8')}
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-primary-400 dark:text-primary-500 pointer-events-none">
                  %
                </span>
              </div>
            </div>
          )}
        </form.AppField>

        <form.AppField name="stage_id">
          {(field) => (
            <div
              className="flex flex-col gap-2"
              data-field-id={
                scrollToFieldId === 'stage_id' ? 'stage_id' : undefined
              }
            >
              <Label required>Stage</Label>
              <field.StageFieldControl />
            </div>
          )}
        </form.AppField>

        <form.AppField name="tag_ids">
          {(field) => (
            <div
              className="flex flex-col gap-2"
              data-field-id={
                scrollToFieldId === 'tag_ids' ? 'tag_ids' : undefined
              }
            >
              <Label>Tags</Label>
              <field.TagsFieldControl />
            </div>
          )}
        </form.AppField>

        {includeType && (
          <form.AppField name="type">
            {(field) => (
              <div
                className="flex flex-col gap-2"
                data-field-id={scrollToFieldId === 'type' ? 'type' : undefined}
              >
                <Label required>Type</Label>
                <field.TypeFieldControl />
              </div>
            )}
          </form.AppField>
        )}
      </div>
    </div>
  )
}

// Field sorting comparator
export const compareFieldsByOrder = (
  a: ExtendedSchemaDerivedField,
  b: ExtendedSchemaDerivedField,
): number => {
  const aOrder = a.order ?? null
  const bOrder = b.order ?? null
  if (aOrder !== null && bOrder !== null) {
    return aOrder - bOrder
  } else if (aOrder !== null) {
    return -1
  } else if (bOrder !== null) {
    return 1
  }
  return a.name.localeCompare(b.name)
}

interface DynamicFieldListProps {
  snapshot: ExtendedJsonSchema | undefined
  form: AnyTanstackAppForm
  searchQuery?: string
}

export const DynamicFieldList: React.FC<DynamicFieldListProps> = ({
  snapshot,
  form,
  searchQuery = '',
}) => {
  const sortedFields = useMemo(
    () => (snapshot ? [...snapshot.fields].sort(compareFieldsByOrder) : []),
    [snapshot],
  )

  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return sortedFields
    const lowerQuery = searchQuery.toLowerCase()
    return sortedFields.filter((field) =>
      field.name.toLowerCase().includes(lowerQuery),
    )
  }, [sortedFields, searchQuery])

  if (!snapshot || sortedFields.length === 0) {
    return null
  }

  if (filteredFields.length === 0) {
    return (
      <div className="text-sm text-primary-500 dark:text-primary-400 pb-4">
        No fields match your search.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      {filteredFields.map((field) => {
        const fieldName = `values.${field.id}` as const

        return (
          <div
            key={field.id}
            className="flex flex-col gap-2"
            data-field-id={field.id}
          >
            <form.AppField name={fieldName}>
              {(f) => {
                switch (field.type) {
                  case 'string':
                    return (
                      <f.StringFieldControl
                        label={field.name}
                        required={field.required}
                      />
                    )
                  case 'number': {
                    const numField = field as NumberFieldDerivedV1
                    return (
                      <f.NumberFieldControl
                        label={field.name}
                        required={field.required}
                        min={numField.ge ?? undefined}
                        max={numField.le ?? undefined}
                      />
                    )
                  }
                  case 'boolean':
                    return (
                      <f.BooleanFieldControl
                        label={field.name}
                        required={field.required}
                      />
                    )
                  case 'dropdown': {
                    const dropdownField = field as DropdownFieldDerivedV1
                    return (
                      <f.DropdownFieldControl
                        label={field.name}
                        required={field.required}
                        options={dropdownField.options}
                      />
                    )
                  }
                  case 'multi_select': {
                    const multiSelectField = field as MultiSelectFieldDerivedV1
                    return (
                      <f.MultiSelectFieldControl
                        label={field.name}
                        required={field.required}
                        options={multiSelectField.options}
                      />
                    )
                  }
                  case 'string_list':
                    return (
                      <f.StringListChipsFieldControl
                        label={field.name}
                        required={field.required}
                      />
                    )
                  case 'number_list':
                    return (
                      <f.NumberListChipsFieldControl
                        label={field.name}
                        required={field.required}
                      />
                    )
                  case 'date':
                    return (
                      <f.DateFieldControl
                        label={field.name}
                        required={field.required}
                      />
                    )
                  case 'currency': {
                    const currencyField = field as CurrencyFieldDerivedV1
                    return (
                      <f.CurrencyFieldControl
                        label={field.name}
                        required={field.required}
                        currency={currencyField.currency}
                        min={currencyField.ge ?? undefined}
                        max={currencyField.le ?? undefined}
                      />
                    )
                  }
                  default:
                    return null
                }
              }}
            </form.AppField>
          </div>
        )
      })}
    </div>
  )
}

// Hook for form logic
interface UseEditPursuitFormProps {
  pursuit: Pursuit
  orgId: UUID | ''
  onClose: () => void
  onSuccess?: (pursuit: Pursuit) => void
  stageDueDatesRef: React.RefObject<StageDueDatesSectionRef | null>
}

function useEditPursuitForm({
  pursuit,
  orgId,
  onClose,
  onSuccess,
  stageDueDatesRef,
}: UseEditPursuitFormProps) {
  const { data: schema } = useLatestPursuitSchema({ orgId })
  const { data: stages } = usePursuitStages({ orgId })
  const { mutateAsync: updatePursuit, isPending } = useUpdatePursuit({
    orgId,
  })

  const storedValues = useMemo(
    () => getStoredExtendedValues(pursuit.extended_data),
    [pursuit.extended_data],
  )
  const extendedValuesZodObject = useMemo(
    () => prepareExtendedValuesZodObject(schema?.fields ?? [], storedValues),
    [schema, storedValues],
  )

  const defaultValues: EditPursuitFormValues = useMemo(
    () => ({
      title: pursuit.title,
      description: pursuit.description ?? '',
      stage_id: pursuit.stage.id,
      user_ids: pursuit.user_ids,
      due_date: new Date(pursuit.due_date),
      type: pursuit.type,
      tag_ids: pursuit.tag_ids,
      estimated_value: pursuit.estimated_value ?? null,
      pwin: pursuit.pwin,
      values: schema ? buildDefaultValuesFromPursuit(schema, pursuit) : {},
    }),
    [pursuit, schema],
  )

  const handleSubmit = useCallback(
    async (
      value: EditPursuitFormValues,
      isEdited: (fieldId: string) => boolean,
    ) => {
      try {
        // Only a stage change may move the terminal status; other edits keep No Bid.
        const newStage =
          value.stage_id === pursuit.stage.id
            ? undefined
            : stages?.find((s) => s.id === value.stage_id)
        const terminalStatus = newStage
          ? getTerminalStatusForStage(newStage, pursuit.terminal_status)
          : pursuit.terminal_status

        const updated: Pursuit = {
          ...pursuit,
          title: value.title,
          description: value.description || null,
          stage: { ...pursuit.stage, id: value.stage_id as UUID },
          user_ids: value.user_ids as UUID[],
          due_date: value.due_date,
          type: value.type,
          tag_ids: value.tag_ids,
          estimated_value: value.estimated_value,
          pwin: value.pwin,
          terminal_status: terminalStatus,
          // With no active configuration the form has no custom fields, so keep the pursuit's own.
          extended_schema_id: schema ? schema.id : pursuit.extended_schema_id,
          extended_data: schema
            ? mergeEditedExtendedValues(storedValues, value.values, isEdited)
            : pursuit.extended_data,
        }
        const stageDueDateUpdates = stageDueDatesRef.current?.getUpdates()
        // Only write the roster when the Owners field actually changed: on a
        // private pursuit the roster is the access list, and blind-writing
        // this dialog's (staleTime: Infinity) snapshot on an unrelated edit
        // would revoke members added concurrently by someone else.
        const rosterChanged =
          value.user_ids.length !== pursuit.user_ids.length ||
          !value.user_ids.every((id) => pursuit.user_ids.includes(id as UUID))
        const res = await updatePursuit({
          pursuit: updated,
          writeRoster: rosterChanged,
          stageDueDates:
            stageDueDateUpdates && stageDueDateUpdates.length > 0
              ? stageDueDateUpdates
              : undefined,
        })
        toast.success('Pursuit updated successfully!')
        onSuccess?.(res)
        onClose()
      } catch (e) {
        console.error(e)
      }
    },
    [
      pursuit,
      schema,
      storedValues,
      stages,
      onSuccess,
      onClose,
      updatePursuit,
      stageDueDatesRef,
    ],
  )

  const form = useAppForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onDynamic: ({ formApi }) =>
        formApi.parseValuesWithSchema(
          // @ts-expect-error - a zod object built at runtime isn't typed as the form values
          buildPursuitFormZodObject(
            extendedValuesZodObject(isExtendedFieldEdited(formApi)),
          ),
        ),
    },
    onSubmit: ({ value, formApi }) =>
      handleSubmit(value, isExtendedFieldEdited(formApi)),
  })

  const numErrors = useStore(form.store, (s) => s.errors.length)

  return {
    form,
    schema,
    isPending,
    numErrors,
  }
}

// region: stage due dates section

export interface StageDueDatesSectionRef {
  getUpdates: () => PursuitStageDueDateSet[]
}

interface StageDueDatesSectionProps {
  stages: PursuitStage[]
  existingDueDates: PursuitStageDueDate[]
  scrollToFieldId?: string
}

/** Convert a Date to `YYYY-MM-DDTHH:mm` for datetime-local inputs. */
function toDatetimeLocal(date: Date): string {
  const y = date.getFullYear()
  const mo = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const mi = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${d}T${h}:${mi}`
}

interface StageDueDateItemProps {
  stage: PursuitStage
  dueDate: string
  onDateChange: (stageId: string, value: string) => void
}

const StageDueDateItem: React.FC<StageDueDateItemProps> = ({
  stage,
  dueDate,
  onDateChange,
}) => {
  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onDateChange(stage.id, e.target.value)
    },
    [stage.id, onDateChange],
  )

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const input = (e.currentTarget as HTMLElement).querySelector('input')
    input?.showPicker()
  }, [])

  return (
    <div
      key={stage.id}
      className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900 cursor-pointer"
      onClick={handleClick}
    >
      <ColorIndicator
        color={stage.color as Color}
        className="w-2.5 h-2.5 flex-shrink-0"
      />
      <span className="text-sm text-primary-700 dark:text-primary-300 flex-1 truncate">
        {stage.name}
      </span>
      <div className="flex-1 min-w-0 flex-shrink-0">
        <Input
          type="datetime-local"
          value={dueDate ?? ''}
          onChange={handleDateChange}
          className={twMerge('dark:bg-default-dark text-sm w-full block')}
        />
      </div>
    </div>
  )
}

const StageDueDatesSection = React.forwardRef<
  StageDueDatesSectionRef,
  StageDueDatesSectionProps
>(({ stages, existingDueDates, scrollToFieldId }, ref) => {
  const sortedStages = useMemo(
    () => [...stages].sort((a, b) => a.index - b.index),
    [stages],
  )

  const initialDueDatesRef = useRef<Record<string, string>>({})
  const [dueDates, setDueDates] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const dd of existingDueDates) {
      initial[dd.stage_id] = toDatetimeLocal(new Date(dd.due_date))
    }
    initialDueDatesRef.current = initial
    return initial
  })

  const handleDateChange = useCallback((stageId: string, value: string) => {
    setDueDates((prev) => ({ ...prev, [stageId]: value }))
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      getUpdates: () => {
        const initial = initialDueDatesRef.current
        return sortedStages
          .filter((stage) => {
            const cur = dueDates[stage.id] ?? ''
            const prev = initial[stage.id] ?? ''
            return cur !== prev
          })
          .map((stage) => ({
            stage_id: stage.id,
            due_date: dueDates[stage.id] ? new Date(dueDates[stage.id]) : null,
          }))
      },
    }),
    [sortedStages, dueDates],
  )

  return (
    <div
      className="flex flex-col gap-3"
      data-field-id={
        scrollToFieldId === 'stage_due_dates' ? 'stage_due_dates' : undefined
      }
    >
      <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
        Stage Due Dates
      </span>
      <div className="flex flex-col gap-1">
        {sortedStages.map((stage) => (
          <StageDueDateItem
            key={stage.id}
            stage={stage}
            dueDate={dueDates[stage.id] ?? ''}
            onDateChange={handleDateChange}
          />
        ))}
      </div>
    </div>
  )
})

StageDueDatesSection.displayName = 'StageDueDatesSection'

// endregion: stage due dates section

export const EditPursuitDialog: React.FC<EditPursuitDialogProps> = ({
  open,
  initialValues,
  onOpenChange,
  onSuccess,
  scrollToFieldId,
}) => {
  // Source org from pursuit, not activeOrgId (empty in multi-org overview).
  const pursuitOrgId = initialValues.organization_id

  const overview = useOptionalOrgsOverview()
  const org = overview?.orgsById[pursuitOrgId]

  const { data: stages } = usePursuitStages({ orgId: pursuitOrgId })
  const { data: stageDueDates, isLoading: isLoadingStageDueDates } =
    usePursuitStageDueDates({ pursuitId: initialValues.id as UUID })

  // Increment key each time dialog opens so the section remounts with fresh state
  const prevOpenRef = useRef(false)
  const dueDatesKeyRef = useRef(0)
  if (open && !prevOpenRef.current) {
    dueDatesKeyRef.current += 1
  }
  prevOpenRef.current = open

  const dialogContentRef = useRef<HTMLDivElement>(null)
  const stageDueDatesRef = useRef<StageDueDatesSectionRef>(null)
  const [fieldSearchQuery, setFieldSearchQuery] = useState<string>('')

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleFieldSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldSearchQuery(e.target.value)
    },
    [],
  )

  // Scroll to field when dialog opens and scrollToFieldId is provided
  useEffect(() => {
    if (!open || !scrollToFieldId) return

    // Use a longer delay to ensure dialog content and form fields are fully rendered
    const timeoutId = setTimeout(() => {
      // Query from the dialog content ref, or fallback to document if ref isn't ready
      const container = dialogContentRef.current || document
      const fieldElement = container.querySelector(
        `[data-field-id="${scrollToFieldId}"]`,
      ) as HTMLElement | null

      if (fieldElement) {
        // Scroll the element into view
        fieldElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        })

        // Focus the input after scroll completes
        const input = fieldElement.querySelector(
          'input, textarea, select',
        ) as HTMLElement | null
        if (input) {
          input.focus()
        }
      }
    }, 50)

    return () => clearTimeout(timeoutId)
  }, [open, scrollToFieldId])

  const formResult = useEditPursuitForm({
    pursuit: initialValues,
    orgId: pursuitOrgId,
    onClose: handleClose,
    onSuccess: onSuccess,
    stageDueDatesRef,
  })

  const { form, schema, isPending, numErrors } = formResult
  const formView = form as unknown as AnyTanstackAppForm

  const handleCancel = useCallback(() => {
    handleClose()
  }, [handleClose])

  const handleSaveAll = useCallback(() => {
    form.handleSubmit()
  }, [form])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogContentRef}
        aria-describedby={undefined}
        className="sm:min-w-[600px] max-w-[700px] p-0 max-h-[90vh] flex flex-col"
      >
        <div className="flex-1 overflow-y-auto p-5">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>Edit Pursuit</DialogTitle>
              {org && (
                <OrgBadge
                  organization={org}
                  className="max-w-[200px] mr-8"
                  avatarSize={16}
                />
              )}
            </div>
            <DialogDescription className="space-y-2">
              <p>
                Update pursuit details including stage, assignees, due dates,
                and custom field values.
              </p>
              <p className="text-sm text-primary-600 dark:text-primary-400">
                You can modify any field below, including both
                Sweetspot-provided fields and any custom fields configured for
                your organization.
              </p>
            </DialogDescription>
          </DialogHeader>
          <form.AppForm>
            <div className="flex flex-col gap-6 my-5">
              <OverviewSection
                form={formView}
                includeType={false}
                scrollToFieldId={scrollToFieldId}
              />

              {stages && !isLoadingStageDueDates && (
                <>
                  <Separator />
                  <StageDueDatesSection
                    key={dueDatesKeyRef.current}
                    ref={stageDueDatesRef}
                    stages={stages}
                    existingDueDates={stageDueDates ?? []}
                    scrollToFieldId={scrollToFieldId}
                  />
                </>
              )}

              {schema && schema.fields.length > 0 && (
                <>
                  <Separator />
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between w-full gap-2 -mt-2">
                      <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                        Custom Fields ({schema.fields.length})
                      </span>
                      <SearchInput
                        placeholder="Search fields..."
                        value={fieldSearchQuery}
                        onChange={handleFieldSearchChange}
                        className="w-48"
                      />
                    </div>
                    <div className="pt-2">
                      <DynamicFieldList
                        snapshot={schema}
                        form={formView}
                        searchQuery={fieldSearchQuery}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </form.AppForm>
        </div>

        <div className="flex items-center justify-between gap-2 w-full p-5 border-t border-primary-200 dark:border-primary-800">
          <div className="flex-1">
            {numErrors > 0 && (
              <ErrorMessage className="self-center">
                Please review and correct the fields with errors.
              </ErrorMessage>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline-compact"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="primary-compact"
              disabled={isPending}
              isLoading={isPending}
              onClick={handleSaveAll}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
